import { PortalTelemetryEvent } from '../../app/telemetry/portalTelemetryEvents';
import { PortalTelemetryScreen } from '../../app/telemetry/portalTelemetryScreens';
import { validationIssues } from '../../app/telemetry/validationTelemetry';
import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { portalScreenContext, trackPortalEvent, portalAction } from '../../app/telemetry/portalTelemetry';
import { Box, Button, Grid, Paper } from "@mui/material";
import AppTextInput from "../../app/components/AppTextInput";
import { FormProvider, useForm } from "react-hook-form";
import EventsFormModel from "../../app/models/events/eventsFormModel";
import { yupResolver } from "@hookform/resolvers/yup";
import { CalendarOfActivitiesValidationSchemas } from "./common/eventApprovalValidationSchemas";
import { MESSAGE_SAVE_AND_CONTINUE, MESSAGE_NEXT, API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_PROCESSING_DETAILS } from "../../app/utils/constant";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { PcsEventDto } from "../../app/models/events/pcsEventDto";
import { updatePcsEventAsync, gePcsEventByIdAsync } from "../../app/slices/pcsEventSlice";
import { useAppSelector, useAppDispatch } from "../../app/store/configureStore";
import PcsEventDialog from "./common/PcsEventDialog";
import { YesNoEnum } from "../../app/enums/yesNoEnum";
import AppRadioButtonsGroup from "../../app/components/AppRadioButtonsGroup";
import { yesNoArray } from "../../app/utils/util";

const { EventPublishedOnRcsiCalendarOfActivities, EventWebsiteUrl, EmailAddress, ApprovalToAnotherPostgraduateMedicalTrainingBody
} = EventsFormModel.calendarOfActivitiesFormField;

interface Props {
    isSpaEditabe: boolean;
    handleNext: () => void;
    handleBack: () => void;
}

export default function CalendarOfActivities({ isSpaEditabe, handleNext, handleBack }: Props) {
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(CalendarOfActivitiesValidationSchemas)
    });
    const formObj = methods.watch();
    const { control, reset, trigger, getValues, watch, formState: { isDirty } } = methods;

    const { pcsEventDetails, getPcsEventStatus, updatePcsEventStatus } = useAppSelector(state => state.pcsEvent);

    const [showApprovalError, setShowApprovalError] = useState(false);
    const [eventId,] = useState(new URLSearchParams(window.location.search).get("eventId"));

    useEffect(() => {
        if (pcsEventDetails !== null && eventId !== null && eventId !== '') {
            reset({
                [EventPublishedOnRcsiCalendarOfActivities.name]: pcsEventDetails?.publish === true ? YesNoEnum.YES : YesNoEnum.NO,
                [EventWebsiteUrl.name]: pcsEventDetails?.eventUrl,
                [EmailAddress.name]: pcsEventDetails?.contactEmail,
                [ApprovalToAnotherPostgraduateMedicalTrainingBody.name]: pcsEventDetails?.appliedToOther === true ? YesNoEnum.YES : YesNoEnum.NO
            });
        }
    }, [reset, pcsEventDetails, eventId]);

    const MapToPcsEvent = (values: any) => {
        const pcsEventDtoObj: PcsEventDto = {
            ...pcsEventDetails,
            publish: values.EventPublishedOnRcsiCalendarOfActivities === YesNoEnum.YES,
            eventUrl: values.EventWebsiteUrl,
            contactEmail: values.EmailAddress,
            appliedToOther: values.ApprovalToAnotherPostgraduateMedicalTrainingBody === YesNoEnum.YES
        }
        return pcsEventDtoObj;
    }

    const dispatch = useAppDispatch();

    const saveDetails = async () => {
        const isStepValid = await trigger();
        if (!isStepValid) {
            trackPortalEvent(portalScreenContext(PortalTelemetryScreen.CalendarOfActivities),
                PortalTelemetryEvent.ValidationBlocked, {
                step: 'activities',
                validationErrors: validationIssues(PortalTelemetryScreen.CalendarOfActivities, methods.getFieldState)
            });
        }

        if (isStepValid && isDirty === true) {
            dispatch(updatePcsEventAsync(MapToPcsEvent(formObj)))
                .then((response) => {
                    if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                        pcsEventDetails?.eventId && dispatch(gePcsEventByIdAsync(pcsEventDetails?.eventId));
                        toast.success(MESSAGE_DETAILS_SAVED);
                        handleNext();
                    }
                });
        }

        if (isStepValid && isDirty === false) {
            handleNext();
        }
    }

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name === ApprovalToAnotherPostgraduateMedicalTrainingBody.name && type === 'change') {
                setShowApprovalError(value.ApprovalToAnotherPostgraduateMedicalTrainingBody === YesNoEnum.YES);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    usePortalScreen(portalScreenContext(PortalTelemetryScreen.CalendarOfActivities), undefined, !(getPcsEventStatus === ApiRequestStatus.Pending || updatePcsEventStatus === ApiRequestStatus.Pending));

    if (getPcsEventStatus === ApiRequestStatus.Pending || updatePcsEventStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <FormProvider {...methods}>
        <Box sx={{ minWidth: "100%" }} component={Paper}>
            <Grid container rowSpacing={4} sx={{ ml: '2%' }}>
                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 12 }}>
                    <AppRadioButtonsGroup control={control} name={EventPublishedOnRcsiCalendarOfActivities.name}
                        label={EventPublishedOnRcsiCalendarOfActivities.label} items={yesNoArray} disabled={isSpaEditabe} />
                </Grid>

                {getValues(EventPublishedOnRcsiCalendarOfActivities.name as keyof PcsEventDto)?.toUpperCase() === YesNoEnum.YES && <>
                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={EventWebsiteUrl.name} label={EventWebsiteUrl.label}
                            maxStringCount={EventWebsiteUrl.maxStringCount} disabled={isSpaEditabe} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={EmailAddress.name} label={EmailAddress.label}
                            maxStringCount={EmailAddress.maxStringCount} disabled={isSpaEditabe} />
                    </Grid>
                </>}
                <Grid size={{ xs: 12 }}>
                    <AppRadioButtonsGroup control={control} name={ApprovalToAnotherPostgraduateMedicalTrainingBody.name}
                        label={ApprovalToAnotherPostgraduateMedicalTrainingBody.label} items={yesNoArray} disabled={isSpaEditabe} />
                </Grid>

                <Grid size={{ xs: 5 }} sx={{ textAlign: 'start' }}>
                    <Button variant="outlined" color="primary" onClick={portalAction(portalScreenContext(PortalTelemetryScreen.CalendarOfActivities), `${PortalTelemetryScreen.CalendarOfActivities}.onClick.handleBack`, handleBack)}>
                        Back to previous step
                    </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: 'end' }}>
                    <Button variant="contained" color="primary" onClick={saveDetails}
                        disabled={getValues(ApprovalToAnotherPostgraduateMedicalTrainingBody.name as keyof PcsEventDto)?.toUpperCase() === YesNoEnum.YES}>
                        {isDirty === true ? MESSAGE_SAVE_AND_CONTINUE : MESSAGE_NEXT}
                    </Button>
                </Grid>

                <Grid size={{ xs: 1 }}></Grid>

                <Grid size={{ xs: 12 }}></Grid>
            </Grid>
        </Box>

        <PcsEventDialog showDialog={showApprovalError}
            title=''
            content={<>
                It is not necessary to proceed with this application.
                <br></br><br></br>
                Reciprocal arrangements are in place between the Postgraduate Training Bodies, whereby a CPD certificate of attendance for an event or activity granted by one Postgraduate Training Body or Faculty will be automatically accepted by the other training bodies as evidence of a doctor's participation in that activity.
                <br></br><br></br>
                CPD approval should be sought from a single Postgraduate Training Body - specifically, the one most closely aligned with the intended audience for the activity.
                <br></br><br></br>
                There is no requirement to submit duplicate applications to other training bodies.
            </>}
            skipButtonClick={portalAction(portalScreenContext(PortalTelemetryScreen.CalendarOfActivities), `${PortalTelemetryScreen.CalendarOfActivities}.skipButtonClick.setShowApprovalError`, () => setShowApprovalError(false))}
            showYesNoButtons={false} />

    </FormProvider>
}
